import React, { useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, PageHero } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ContainerSizingGuidePage.module.css';

const DOCS = '/static/documents/container-sizing-guide';
const IMG = '/static/images/container-guide';

// ---------- Accordion item ----------

const AccordionItem = ({ id, title, number, open, onToggle, children }) => {
  return (
    <div className={css.accordionCard} id={id}>
      <button
        type="button"
        className={css.accordionTrigger}
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={`panel-${id}`}
        id={`trigger-${id}`}
      >
        {number && <span className={css.accordionNumber}>{number}</span>}
        <h3 className={css.accordionTitle}>{title}</h3>
        <span className={`${css.chevron} ${open ? css.chevronOpen : ''}`} />
      </button>
      <div
        className={`${css.accordionPanelWrap} ${open ? css.accordionPanelOpen : ''}`}
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`trigger-${id}`}
      >
        <div className={css.accordionPanel}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ---------- Page schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'HowTo',
    name: 'How to Size a Skydiving Container',
    url: `${rootURL}/container-sizing`,
    description:
      'Comprehensive guide to determining the correct container size for skydiving rigs from major manufacturers: UPT, Sun Path, Mirage, Aerodyne, Wings, Paratec, Rigging Innovation, SWS, and Firebird.',
    totalTime: 'PT20M',
    supply: [
      { '@type': 'HowToSupply', name: 'Intended main canopy model and size (sqft)' },
      { '@type': 'HowToSupply', name: 'Intended reserve canopy model and size (sqft)' },
    ],
    step: [
      {
        '@type': 'HowToStep',
        name: 'Understand Container Sizing Basics',
        text:
          'Ensure the rig you are buying can hold the canopy sizes that you are jumping. A rig typically holds canopy sizes within about 40 sqft. Factors such as material, number of cells, and crossbracing affect pack volume.',
      },
      {
        '@type': 'HowToStep',
        name: 'Select Your Manufacturer',
        text:
          'Choose from leading manufacturers like UPT, Sun Path, Mirage, Aerodyne, Wings, Paratec, Rigging Innovation, SWS, or Firebird to view specific sizing charts and compatibility details.',
      },
      {
        '@type': 'HowToStep',
        name: 'Consult Manufacturer Sizing Charts',
        text:
          'Use the official sizing charts to confirm that the container size suits the canopy combination you intend to jump. Consult a rigger when in doubt.',
      },
    ],
    tool: [
      { '@type': 'HowToTool', name: 'UPT Canopy Sizing Chart' },
      { '@type': 'HowToTool', name: 'Sun Path Canopy Compatibility Chart' },
      { '@type': 'HowToTool', name: 'Mirage Container Sizing Guide' },
      { '@type': 'HowToTool', name: 'Aerodyne Icon Sizing Chart' },
      { '@type': 'HowToTool', name: 'Wings Container Sizing Chart' },
      { '@type': 'HowToTool', name: 'Paratec Next Sizing Chart' },
      { '@type': 'HowToTool', name: 'Rigging Innovation Volume Charts' },
      { '@type': 'HowToTool', name: 'SWS Sizing Guide' },
      { '@type': 'HowToTool', name: 'Firebird Evo Sizing Chart' },
    ],
    mentions: [
      { '@type': 'Organization', name: 'United Parachute Technologies' },
      { '@type': 'Organization', name: 'Sun Path Products' },
      { '@type': 'Organization', name: 'Mirage Systems' },
      { '@type': 'Organization', name: 'Aerodyne Research' },
      { '@type': 'Organization', name: 'SunRise Rigging (Wings)' },
      { '@type': 'Organization', name: 'Paratec' },
      { '@type': 'Organization', name: 'Rigging Innovations' },
      { '@type': 'Organization', name: 'SWS Skydiving' },
      { '@type': 'Organization', name: 'Firebird Skydiving' },
    ],
    about: {
      '@type': 'Thing',
      name: 'Skydiving container / rig canopy compatibility',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Skydivers choosing a rig to fit a given canopy size',
    },
    inLanguage: 'en',
  };
}

function buildFaqSchema() {
  return {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I determine the correct container size for my skydiving rig?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Ensure that the rig you are buying can hold the canopy sizes you plan to jump. Typically, a rig holds canopy sizes within about 40 sqft. Pack volume varies depending on material, number of cells, and crossbracing. Always check manufacturer sizing charts and consult your rigger.',
        },
      },
      {
        '@type': 'Question',
        name: 'What factors affect canopy pack volume?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Canopy pack volume depends on material type (ZP, ZPX, Ultra LPV), number of cells, crossbracing, and other design features.',
        },
      },
    ],
  };
}

// ---------- Main page ----------

const layoutAreas = `
  topbar
  main
  footer
`;

const NAV_ITEMS = [
  { id: 'upt', num: '01', label: 'UPT' },
  { id: 'sunpath', num: '02', label: 'Sun Path' },
  { id: 'mirage', num: '03', label: 'Mirage' },
  { id: 'aerodyne', num: '04', label: 'Aerodyne' },
  { id: 'wings', num: '05', label: 'Wings' },
  { id: 'paratec', num: '06', label: 'Paratec' },
  { id: 'rigging-innovation', num: '07', label: 'Rigging Innovation' },
  { id: 'sws', num: '08', label: 'SWS' },
  { id: 'firebird', num: '09', label: 'Firebird' },
];

const SECTION_IDS = NAV_ITEMS.map(i => i.id);

const ContainerSizingGuidePageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const [openSections, setOpenSections] = useState(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash.slice(1);
      if (SECTION_IDS.includes(hash)) return { [hash]: true };
    }
    return {};
  });

  const toggleSection = id => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openAndScrollTo = id => {
    setOpenSections(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Skydiving Container Sizing Guide | Avento';
  const schemaDescription =
    'Find the right container size for your canopies. Main & reserve sizing charts for UPT, Sun Path, Mirage, Aerodyne, Wings, Paratec, Rigging Innovation, SWS and Firebird.';

  const pageSchema = buildPageSchema(rootURL);
  const faqSchema = buildFaqSchema();
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Container Sizing Guide', item: `${rootURL}/container-sizing` },
    ],
  };

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[pageSchema, faqSchema, breadcrumbSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="ContainerSizingGuidePage" />
              </Topbar>
              <Main as="main" className={css.main}>

      {/* Hero */}
      <PageHero
        title="Container Sizing Guide"
        narrowTitle
        subtitle="Find out what canopies fit your rig. A bundle of information on container sizes of the largest manufacturers."
        desktopImage={`${IMG}/hero-desktop.webp`}
        desktopWidth={3024}
        desktopHeight={700}
        mobileImage={`${IMG}/hero-mobile.webp`}
        mobileWidth={780}
        mobileHeight={571}
      />

      {/* Introduction */}
      <section className={css.section}>
        <div className={css.container}>
          <h2 className={css.sectionHeading}>Introduction</h2>
          <p className={css.paragraph}>
            It is important to make sure that the rig you are buying can hold the canopy sizes that you are jumping. Usually, a rig holds canopy sizes in a range of around 40 sqft - so keep in mind the possibility to downsize with the rig.
          </p>
          <p className={css.paragraph}>
            Of course, not all canopies of the same size have the same pack volume. The material, number of cells, crossbracing and other factors play a role. In this guide, we compile the manufacturer information on which container size fits which canopies. As always: when in doubt, ask your rigger!
          </p>
        </div>
      </section>

      {/* Horizontal section navigation */}
      <div className={css.sectionNavWrapper}>
        <nav className={css.sectionNav} aria-label="Manufacturers">
          {NAV_ITEMS.map(item => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={css.navItem}
              onClick={e => {
                e.preventDefault();
                openAndScrollTo(item.id);
              }}
            >
              <span className={css.navNumber}>{item.num}</span> {item.label}
            </a>
          ))}
        </nav>
      </div>

      {/* Manufacturers */}
      <section className={`${css.section} ${css.lastSection}`} id="manufacturers">
        <div className={css.container}>
          <h2 className={css.sectionHeading}>Manufacturers</h2>
          <p className={css.paragraph}>
            Select the manufacturer of the rig you are looking to buy below and follow the instructions to verify if the container size is suitable for the canopies you are looking to jump.
          </p>
          <div className={css.accordion}>

            {/* ----- UPT ----- */}
            <AccordionItem id="upt" title="UPT" number="01" open={!!openSections.upt} onToggle={toggleSection}>
              <p className={css.panelText}>
                UPT has a well-structured Canopy Sizing Chart on their website. Just click the button and follow the instructions:
              </p>
              <a
                href="https://uptvector.com/sizing-chart/#toggle-id-1"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                UPT Chart
              </a>
            </AccordionItem>

            {/* ----- Sun Path ----- */}
            <AccordionItem id="sunpath" title="Sun Path" number="02" open={!!openSections.sunpath} onToggle={toggleSection}>
              <p className={css.panelText}>
                Sunpath has a well-structured Canopy Sizing Chart on their website. Just click the button and follow the instructions:
              </p>
              <a
                href="https://www.sunpath.com/designer/canopy-compatibility/"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Sun Path Chart
              </a>
            </AccordionItem>

            {/* ----- Mirage ----- */}
            <AccordionItem id="mirage" title="Mirage" number="03" open={!!openSections.mirage} onToggle={toggleSection}>
              <p className={css.panelText}>
                Mirage has a well-structured Canopy Sizing Chart on their website. Just click the button and follow the instructions:
              </p>
              <a
                href="https://miragesys.com/support/container-sizing-guide/"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Mirage Chart
              </a>
            </AccordionItem>

            {/* ----- Aerodyne ----- */}
            <AccordionItem id="aerodyne" title="Aerodyne" number="04" open={!!openSections.aerodyne} onToggle={toggleSection}>
              <p className={css.panelText}>
                Aerodyne has a document showing fitting canopies for each container size.
              </p>
              <ul className={css.panelList}>
                <li className={css.panelListItem}>
                  <strong>ZP:</strong> Normal material (zero porosity / f111) - most canopies
                </li>
                <li className={css.panelListItem}>
                  <strong>ZPX:</strong> Special material that packs lower - some canopies from Aerodyne or the PD Pulse
                </li>
                <li className={css.panelListItem}>
                  <strong>Ultra LPV:</strong> Extra LPV - some Aerodyne Pilots are Ultra LPV
                </li>
              </ul>
              <a
                href="https://www.flyaerodyne.com/e_files/content/pdf/Icon_Canopy_Sizing_Chart.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Aerodyne Sizing
              </a>
            </AccordionItem>

            {/* ----- Wings ----- */}
            <AccordionItem id="wings" title="Wings" number="05" open={!!openSections.wings} onToggle={toggleSection}>
              <p className={css.panelText}>
                Wings only shows fitting canopies on the example of the PD Reserve and the PD Sabre. Both are pretty standard fitting canopies. So keep in mind that if you have a low-pack-volume reserve like the PD Optimum, you might be able to fit a slightly larger one and if you jump a low-pack-volume main (like a 7 cell) you also might be able to fit a slightly larger one.
              </p>
              <a
                href={`${DOCS}/wings-container_canopy-sizing.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Wings Sizing
              </a>
            </AccordionItem>

            {/* ----- Paratec ----- */}
            <AccordionItem id="paratec" title="Paratec" number="06" open={!!openSections.paratec} onToggle={toggleSection}>
              <p className={css.panelText}>
                Paratec has a document showing examples of fitting canopies for each container size.
              </p>
              <a
                href={`${DOCS}/paratec-next-container-sizing-chart.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Paratec Sizing
              </a>
            </AccordionItem>

            {/* ----- Rigging Innovation ----- */}
            <AccordionItem
              id="rigging-innovation"
              title="Rigging Innovation"
              number="07"
              open={!!openSections['rigging-innovation']}
              onToggle={toggleSection}
            >
              <p className={css.panelText}>
                Rigging Innovation has documents showing examples of fitting canopies for each container size. Choose the model you are looking to buy.
              </p>
              <div className={css.buttonGroup}>
                <a
                  href={`${DOCS}/curv_volume_chart_2019.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.ctaButton}
                >
                  Curv
                </a>
                <a
                  href={`${DOCS}/CPX_volume_chart_2019.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.ctaButton}
                >
                  CPX
                </a>
                <a
                  href={`${DOCS}/RI_Volume_Chart.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.ctaButton}
                >
                  Others
                </a>
              </div>
            </AccordionItem>

            {/* ----- SWS ----- */}
            <AccordionItem id="sws" title="SWS" number="08" open={!!openSections.sws} onToggle={toggleSection}>
              <p className={css.panelText}>
                SWS has a well-structured Canopy Sizing Chart showing examples of fitting canopies on their website. Just click the button and follow the instructions.
              </p>
              <a
                href="https://sws.aero/en/procedures/sizing-guide"
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                SWS Chart
              </a>
            </AccordionItem>

            {/* ----- Firebird ----- */}
            <AccordionItem id="firebird" title="Firebird" number="09" open={!!openSections.firebird} onToggle={toggleSection}>
              <p className={css.panelText}>
                Firebird has a document showing fitting canopy sizes for each container size.
              </p>
              <a
                href={`${DOCS}/evo-sizing.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaButton}
              >
                Firebird Sizing
              </a>
            </AccordionItem>

          </div>
        </div>
      </section>

              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ContainerSizingGuidePage = connect(mapStateToProps)(ContainerSizingGuidePageComponent);

export default ContainerSizingGuidePage;
